import React from 'react';
import {
  StyleSheet,
  View,
  Alert,
} from 'react-native';
import { ExcelUploadComponent } from '../../components/upload/ExcelUploadComponent';
import { useVehicleStore } from '../../stores/vehicleStore';
import { supabase } from '../../api/supabase';

const VehicleExcelUploadScreen: React.FC = () => {
  const { addVehicle, vehicles } = useVehicleStore();

  // Expected column mappings for vehicles
  const vehicleColumnMappings = {
    'License Plate': 'licensePlate',
    'Plate Number': 'licensePlate',
    'Plate': 'licensePlate',
    'License Number': 'licensePlate',
    'Make Model': 'makeModel',
    'Make & Model': 'makeModel',
    'Vehicle Model': 'makeModel',
    'Car Model': 'makeModel',
    'Year': 'year',
    'Model Year': 'year',
    'Manufacturing Year': 'year',
    'VIN Number': 'vinNumber',
    'VIN': 'vinNumber',
    'Chassis Number': 'vinNumber',
    'Chassis': 'vinNumber',
    'Car Status': 'carStatus',
    'Status': 'carStatus',
    'Vehicle Status': 'carStatus',
    'Current Driver': 'currentDriverId',
    'Driver': 'currentDriverId',
    'Assigned Driver': 'currentDriverId',
    'Daily Rent': 'dailyRent',
    'Daily Rate': 'dailyRent',
    'Company Share': 'dailyRent',
    'Insurance Expiry': 'insuranceExpiry',
    'Insurance Date': 'insuranceExpiry',
    'Registration Expiry': 'registrationExpiry',
    'Registration Date': 'registrationExpiry',
    'Notes': 'notes',
    'Remarks': 'notes',
    'Comments': 'notes',
    'Image URL': 'image_url',
    'Photo': 'image_url',
    'Picture': 'image_url',
  };

  const expectedColumns = [
    'licensePlate',
    'makeModel', 
    'year',
    'vinNumber',
    'carStatus',
    'dailyRent',
    'insuranceExpiry',
    'registrationExpiry',
    'notes'
  ];

  const sampleData = {
    'License Plate': 'ABC123',
    'Make Model': 'Toyota Camry',
    'Year': '2022',
    'VIN Number': 'WDB1234567890',
    'Car Status': 'available',
    'Daily Company Share': '80',
    'Insurance Expiry': '2024-12-31',
    'Registration Expiry': '2024-11-30',
    'Notes': 'Excellent condition'
  };

  const processVehicleData = async (data: any[]) => {
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    for (const [index, row] of data.entries()) {
      try {
        // Validate required fields
        if (!row.licensePlate) {
          throw new Error('License plate is required');
        }

        if (!row.makeModel) {
          throw new Error('Make/Model is required');
        }

        // Check for duplicates
        const existingVehicle = vehicles.find(v => 
          v.licensePlate.toLowerCase() === row.licensePlate.toLowerCase()
        );

        if (existingVehicle) {
          // Update existing vehicle
          await updateExistingVehicle(existingVehicle.id, row);
          successCount++;
        } else {
          // Create new vehicle
          await createNewVehicle(row);
          successCount++;
        }

      } catch (error) {
        errorCount++;
        errors.push(`Row ${row._rowNumber || index + 1}: ${error.message}`);
      }
    }

    // Show results
    if (errorCount > 0) {
      Alert.alert(
        'Upload Completed with Errors',
        `Successfully processed: ${successCount}\nErrors: ${errorCount}\n\nFirst few errors:\n${errors.slice(0, 5).join('\n')}`,
        [{ text: 'OK' }]
      );
    } else {
      Alert.alert(
        'Upload Successful',
        `Successfully processed ${successCount} vehicles!`,
        [{ text: 'OK' }]
      );
    }
  };

  const createNewVehicle = async (vehicleData: any) => {
    // Prepare vehicle data for database
    const dbVehicleData = {
      license_plate: vehicleData.licensePlate,
      make_model: vehicleData.makeModel,
      year: vehicleData.year ? parseInt(vehicleData.year) : null,
      vin_number: vehicleData.vinNumber || null,
      car_status: normalizeCarStatus(vehicleData.carStatus) || 'available',
      daily_rent: vehicleData.dailyRent ? parseFloat(vehicleData.dailyRent) : null,
      insurance_expiry: parseDate(vehicleData.insuranceExpiry),
      registration_expiry: parseDate(vehicleData.registrationExpiry),
      notes: vehicleData.notes || null,
      image_url: vehicleData.image_url || null,
    };

    // Insert into database
    const { data, error } = await supabase
      .from('vehicles')
      .insert(dbVehicleData)
      .select()
      .single();

    if (error) {
      throw new Error(`Database error: ${error.message}`);
    }

    // Add to local store
    const formattedVehicle = {
      id: data.id,
      licensePlate: data.license_plate,
      makeModel: data.make_model,
      year: data.year,
      vinNumber: data.vin_number,
      image_url: data.image_url,
      carStatus: data.car_status as any,
      currentDriverId: data.current_driver_id,
      dailyRent: data.daily_rent,
      insuranceExpiry: data.insurance_expiry,
      registrationExpiry: data.registration_expiry,
      notes: data.notes,
      created_at: data.created_at,
    };

    // Update the store (this will add to the vehicles array)
    addVehicle(formattedVehicle);
  };

  const updateExistingVehicle = async (vehicleId: string, vehicleData: any) => {
    const updateData: any = {};

    // Only update fields that have values
    if (vehicleData.licensePlate) updateData.license_plate = vehicleData.licensePlate;
    if (vehicleData.makeModel) updateData.make_model = vehicleData.makeModel;
    if (vehicleData.year) updateData.year = parseInt(vehicleData.year);
    if (vehicleData.vinNumber) updateData.vin_number = vehicleData.vinNumber;
    if (vehicleData.carStatus) updateData.car_status = normalizeCarStatus(vehicleData.carStatus);
    if (vehicleData.dailyRent) updateData.daily_rent = parseFloat(vehicleData.dailyRent);
    if (vehicleData.insuranceExpiry) updateData.insurance_expiry = parseDate(vehicleData.insuranceExpiry);
    if (vehicleData.registrationExpiry) updateData.registration_expiry = parseDate(vehicleData.registrationExpiry);
    if (vehicleData.notes) updateData.notes = vehicleData.notes;
    if (vehicleData.image_url) updateData.image_url = vehicleData.image_url;

    updateData.updated_at = new Date().toISOString();

    const { error } = await supabase
      .from('vehicles')
      .update(updateData)
      .eq('id', vehicleId);

    if (error) {
      throw new Error(`Database update error: ${error.message}`);
    }
  };

  const normalizeCarStatus = (status: string): string => {
    if (!status) return 'available';
    
    const statusLower = status.toLowerCase().trim();
    
    if (statusLower.includes('work') || statusLower.includes('working')) return 'working';
    if (statusLower.includes('stop') || statusLower.includes('stopped')) return 'stopped';
    if (statusLower.includes('cancel') || statusLower.includes('cancelled')) return 'cancelled';
    if (statusLower.includes('available') || statusLower.includes('ready')) return 'available';
    if (statusLower.includes('maintenance') || statusLower.includes('repair')) return 'maintenance';
    
    return 'available'; // Default
  };

  const parseDate = (dateString: string): string | null => {
    if (!dateString) return null;
    
    try {
      // Handle Excel date serial numbers
      if (/^\d{5}$/.test(dateString)) {
        const excelDate = new Date((parseInt(dateString) - 25569) * 86400 * 1000);
        return excelDate.toISOString().split('T')[0];
      }
      
      // Handle various date formats
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return null;
      }
      
      return date.toISOString().split('T')[0];
    } catch {
      return null;
    }
  };

  return (
    <View style={styles.container}>
      <ExcelUploadComponent
        title="Vehicle Excel Upload"
        description="Upload vehicle data from Excel files (.xlsx, .xls) or CSV files. The system will automatically map columns and handle duplicates."
        expectedColumns={expectedColumns}
        columnMappings={vehicleColumnMappings}
        onDataProcessed={processVehicleData}
        sampleData={sampleData}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
});

export default VehicleExcelUploadScreen;
